import React, { useMemo } from "react";
import "./TotalCountRings.scss";

export default function TotalCountRings({
  counts = { projects: 0, tasks: 0, todos: 0 },
  title = "Work Summary",
  showCenterTotal = true,
}) {
  const vbSize = 200;
  const center = vbSize / 2;
  const stroke = 13;

  const rings = useMemo(() => {
    const p = Number(counts.projects ?? 0);
    const t = Number(counts.tasks ?? 0);
    const d = Number(counts.todos ?? 0);
    return [
      {
        key: "projects",
        value: p,
        color: "#6c63ff",
        trackColor: "#ededfd",
        radius: 74,
        label: "Projects",
        icon: "🗂️",
      },
      {
        key: "stages",
        value: t,
        color: "#fb8c00",
        trackColor: "#fff3e0",
        radius: 52,
        label: "Stages",
        icon: "📋",
      },
      {
        key: "tasks",
        value: d,
        color: "#4aa0f0",
        trackColor: "#e3f2fd",
        radius: 30,
        label: "Tasks",
        icon: "✅",
      },
    ];
  }, [counts]);

  const total = useMemo(
    () =>
      rings.reduce(
        (acc, r) => acc + (Number.isFinite(r.value) ? r.value : 0),
        0,
      ),
    [rings],
  );

  const safeTotal = total || 1;

  return (
    <div
      className="tcr-wrapper"
      aria-label="Work summary: projects, stages, and tasks"
    >
      {/* Header */}
      <div className="tcr-header">
        <span className="tcr-title">{title}</span>
        <span className="tcr-total-badge">{total} total</span>
      </div>

      {/* Body: legend left, donut right */}
      <div className="tcr-body">
        <div className="tcr-left" aria-label="Summary details">
          {rings.map((r) => {
            const pct = Math.round((r.value / safeTotal) * 100);
            return (
              <div key={r.key} className="tcr-legend-item">
                <span className="tcr-dot" style={{ background: r.color }} />
                <div className="tcr-legend-main">
                  <span className="tcr-legend-label">{r.label}</span>
                  <div className="tcr-legend-bar-track">
                    <div
                      className="tcr-legend-bar-fill"
                      style={{ width: `${pct}%`, background: r.color }}
                    />
                  </div>
                </div>
                <div className="tcr-legend-values">
                  <span className="tcr-count">{r.value}</span>
                  <span className="tcr-pct">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="tcr-right">
          <svg
            className="tcr-svg"
            viewBox={`0 0 ${vbSize} ${vbSize}`}
            role="img"
            aria-label="Donut chart of work summary"
          >
            {rings.map((r) => {
              const circumference = 2 * Math.PI * r.radius;
              const progress = r.value / safeTotal;
              const dash = circumference * progress;
              const gap = circumference - dash;

              return (
                <g key={r.key} transform={`rotate(-90 ${center} ${center})`}>
                  <circle
                    cx={center}
                    cy={center}
                    r={r.radius}
                    stroke={r.trackColor}
                    strokeWidth={stroke}
                    fill="none"
                  />
                  <circle
                    cx={center}
                    cy={center}
                    r={r.radius}
                    stroke={r.color}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${dash} ${gap}`}
                    style={{
                      transition:
                        "stroke-dasharray 700ms cubic-bezier(.4,0,.2,1)",
                    }}
                  />
                </g>
              );
            })}

            {showCenterTotal && (
              <>
                <text
                  className="tcr-center-num"
                  x={center}
                  y={center - 4}
                  textAnchor="middle"
                >
                  {total}
                </text>
                <text
                  className="tcr-center-label"
                  x={center}
                  y={center + 14}
                  textAnchor="middle"
                >
                  items
                </text>
              </>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}
